import React, { useRef, ChangeEvent, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import PlusIcon from '@jwp/ott-theme/assets/icons/plus.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';

import ChangeIcon from '../../../../theme/assets/icons/change.svg?react';
import DeleteIcon from '../../../../theme/assets/icons/delete.svg?react';
import { formatTimeMs } from '../../utils/formatTime';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';

import styles from './UploadBox.module.scss';

type Props = {
  onFileSelect: (file: File | null) => void;
  onThumbnailSelect?: (file: File) => void;
  onEditCover?: () => void;
  selectedFile?: File | null;
  poster?: string;
  title?: string;
  hint?: string;
  accept?: string;
  className?: string;
  height?: string | number;
  width?: string | number;
  error?: boolean;
  type?: 'reel' | 'video' | 'image' | 'file';
  onPreview?: () => void;
  disabled?: boolean;
  previewUrl?: string | null;
  duration?: number | null;
  onAddFromGallery?: (file: File) => void;
  onDelete?: () => void;
  readOnly?: boolean;
  buttonAlignment?: 'center' | 'right';
  showControlsOnHover?: boolean;
  label?: string;
  onEdit?: () => void;
  autoPlay?: boolean;
  showVideoControls?: boolean;
  durationShown?: boolean;
  /** Place Edit Cover / Add from Gallery under the preview instead of overlaid */
  actionsBelowPreview?: boolean;
  isChannel?: boolean;
};

const handleBlob = (blob: Blob | null, videoSrc: string, resolve: (value: File) => void, reject: (reason?: Error) => void) => {
  if (blob) {
    const thumbnailFile = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
    resolve(thumbnailFile);
  } else {
    reject(new Error('Canvas to Blob failed'));
  }
  URL.revokeObjectURL(videoSrc);
};

const extractVideoThumbnail = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = URL.createObjectURL(file);
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      // Seek to 0.1s to avoid potential black frame at 0.0s
      video.currentTime = 0.1;
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => handleBlob(blob, video.src, resolve, reject),
        'image/jpeg',
        0.8,
      );
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video'));
    };
  });
};

const UploadBox = ({
  onFileSelect,
  onThumbnailSelect,
  onEditCover,
  selectedFile,
  poster,
  title,
  hint,
  accept = 'video/*,image/*',
  className,
  height,
  width,
  error,
  type = 'file',
  onAddFromGallery,
  onPreview,
  disabled,
  previewUrl: externalPreviewUrl,
  duration: externalDuration,
  readOnly,
  buttonAlignment = 'center',
  onDelete,
  showControlsOnHover,
  label: overlayLabel,
  onEdit,
  autoPlay,
  showVideoControls = true,
  durationShown = true,
  actionsBelowPreview = false,
  isChannel
}: Props) => {
  const { t } = useTranslation('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const [internalPreviewUrl, setInternalPreviewUrl] = useState<string | null>(null);
  const effectivePreviewUrl = externalPreviewUrl || internalPreviewUrl;
  const [fileType, setFileType] = useState<'video' | 'image' | null>(null);
  const [internalDuration, setInternalDuration] = useState<number | null>(null);
  const effectiveDuration = externalDuration || internalDuration;
  const isVideo = fileType === 'video' || (type === 'video' || type === 'reel') || (externalPreviewUrl && !fileType && !['image', 'file'].includes(type));

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setInternalPreviewUrl(url);
      setFileType(selectedFile.type.startsWith('video/') ? 'video' : 'image');
      return () => URL.revokeObjectURL(url);
    }
    setInternalPreviewUrl(null);
    setFileType(null);
    setInternalDuration(null);
  }, [selectedFile]);

  // Safari does not reliably honor the `autoplay` HTML attribute when a
  // <video>'s `src` is swapped dynamically (e.g. a new blob: URL assigned
  // after the element has already mounted, which is exactly what happens
  // here when `effectivePreviewUrl` changes). Chrome/Firefox re-trigger
  // autoplay on src changes automatically; Safari does not. So instead of
  // relying purely on the `autoPlay` attribute, explicitly call `.play()`
  // whenever the preview URL (or autoPlay flag) changes. The play() promise
  // is intentionally swallowed — if the browser still blocks it (e.g. user
  // hasn't interacted with the page yet), we just fall back to the poster
  // frame instead of throwing an unhandled rejection.
  useEffect(() => {
    if (!autoPlay || !isVideo || !effectivePreviewUrl) { return; }

    const videoEl = previewVideoRef.current;
    if (!videoEl) { return; }

    // Ensure muted is set as a real property (not just the JSX attribute)
    // before calling play() — Safari checks the property at play()-time.
    videoEl.muted = true;

    const playPromise = videoEl.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked (e.g. no user interaction yet on Safari) — no-op,
        // poster/first-frame will just remain visible.
      });
    }
  }, [autoPlay, isVideo, effectivePreviewUrl]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);

      if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = URL.createObjectURL(file);
        video.onloadedmetadata = () => {
          setInternalDuration(Math.floor(video.duration * 1000));
          URL.revokeObjectURL(video.src);
        };

        if (onThumbnailSelect) {
          try {
            const thumbnail = await extractVideoThumbnail(file);
            onThumbnailSelect(thumbnail);
          } catch (error) {
            console.error('Failed to extract thumbnail:', error);
          }
        }
      }
    }
  };

  const handleTriggerChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const uploadTitle = title || t('upload_box.add_file');
  const changeLabel = t('upload_box.change', { type: t(type) });
  const showCoverActions = !disabled && !readOnly && (!!onEditCover || !!onAddFromGallery);
  const showOverlayCoverActions = showCoverActions && !actionsBelowPreview;
  const showBelowCoverActions = showCoverActions && actionsBelowPreview;

  return (
    <div
      className={classNames(
        styles.uploadBox,
        {
          [styles.error]: error,
          [styles.hasPreview]: !!effectivePreviewUrl,
          [styles.disabled]: disabled,
          [styles.actionsBelow]: actionsBelowPreview && !!effectivePreviewUrl,
        },
        className,
      )}
      style={actionsBelowPreview && effectivePreviewUrl ? { width, height: 'auto' } : { height, width }}
      onClick={() => !disabled && !effectivePreviewUrl && fileInputRef.current?.click()}
    >
      <input type="file" ref={fileInputRef} className={styles.hiddenInput} accept={accept} onChange={handleFileChange} />

      {!effectivePreviewUrl && (
        <svg className={styles.svgBorder}>
          <rect x="1" y="1" width="calc(100% - 2px)" height="calc(100% - 2px)" rx="16" ry="16" className={styles.rect} />
        </svg>
      )}

      {/* Hidden input for Gallery selection */}
      {onAddFromGallery && (
        <input
          type="file"
          id="gallery-input"
          className={styles.hiddenInput}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              onAddFromGallery(file);
              e.target.value = ''; // Reset to allow same file re-selection
            }
          }}
        />
      )}

      <div className={styles.content}>
        {effectivePreviewUrl ? (
          <>
            <div className={styles.previewContainer} style={actionsBelowPreview ? { height } : undefined}>
              {isVideo ? (
                <video
                  ref={previewVideoRef}
                  className={classNames((isChannel && type !== 'video' ) ? styles.channelPreviewMedia : styles.previewMedia, { [styles.hoverControls]: showControlsOnHover })}
                  src={effectivePreviewUrl}
                  muted
                  autoPlay={autoPlay}
                  loop={autoPlay}
                  playsInline={autoPlay}
                  poster={poster}
                  disablePictureInPicture
                >
                  <track kind="captions" src="" srcLang="en" label={t('upload_box.no_captions')} default />
                </video>
              ) : (
                <img
                  className={classNames(styles.previewMedia, { [styles.hoverControls]: showControlsOnHover })}
                  src={effectivePreviewUrl}
                  alt={t('upload_box.preview_alt')}
                />
              )}

              {overlayLabel && <div className={styles.labelOverlay}>{overlayLabel}</div>}

              <div className={classNames(styles.overlay, { [styles.hoverControls]: showControlsOnHover })}>
                {isVideo && !showControlsOnHover && showVideoControls && durationShown && (
                  <>
                    <div className={styles.duration}>{effectiveDuration ? formatTimeMs(effectiveDuration) : '00:00'}</div>
                    <div className={styles.playIconContainer} onClick={onPreview}>
                      <Icon icon={PlayIcon} className={styles.playIcon} />
                    </div>
                  </>
                )}

                {showControlsOnHover ? (
                  <div className={styles.centeredControls}>
                    {!disabled && !readOnly && (
                      <div className={styles.controlItem} onClick={onEdit || handleTriggerChange}>
                        <div className={styles.controlCircle}>
                          <Icon icon={ChangeIcon} />
                        </div>
                        <span>{t('upload_box.change_short', { defaultValue: 'Change' })}</span>
                      </div>
                    )}
                    {onDelete && !disabled && !readOnly && (
                      <div
                        className={styles.controlItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete();
                        }}
                      >
                        <div className={classNames(styles.controlCircle, styles.deleteCircle)}>
                          <Icon icon={DeleteIcon} />
                        </div>
                        <span>{t('upload_box.delete', { defaultValue: 'Delete' })}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {showOverlayCoverActions && onEditCover && (
                      <Button label={t('upload_box.edit_cover')} variant="mediaOverlay" fullWidth={true} onClick={onEditCover} className={isChannel ? styles.editChannelCoverBtn : styles.editCoverBtn} />
                    )}

                    {showOverlayCoverActions && onAddFromGallery && (
                      <Button
                        label={t('upload_box.add_from_gallery')}
                        variant="mediaOverlay"
                        fullWidth={true}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          document.getElementById('gallery-input')?.click();
                        }}
                        className={isChannel ? styles.addFromGalleryChannelBtn : styles.addFromGalleryBtn}
                      />
                    )}
                    {!onEditCover && !onAddFromGallery && type !== 'reel' && !disabled && !readOnly && (
                      <Button
                        label={changeLabel}
                        variant="mediaOverlay"
                        startIcon={<Icon icon={ChangeIcon} />}
                        onClick={handleTriggerChange}
                        className={classNames(styles.changeBtn, { [styles.alignRight]: buttonAlignment === 'right' })}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {showBelowCoverActions && (
              <div className={styles.actionsBelowRow}>
                {onEditCover && (
                  <Button label={t('upload_box.edit_cover')} variant="mediaOverlay" fullWidth onClick={onEditCover} className={styles.belowActionBtn} />
                )}
                {onAddFromGallery && (
                  <Button
                    label={t('upload_box.add_from_gallery')}
                    variant="mediaOverlay"
                    fullWidth
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      document.getElementById('gallery-input')?.click();
                    }}
                    className={styles.belowActionBtn}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <div className={styles.uploadPrompt}>
            <div className={styles.addFileButton}>
              <Icon icon={PlusIcon} /> <span className={styles.addFileText}>{uploadTitle}</span>
            </div>
            {hint && <p className={styles.hintText}>{hint}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadBox;
